import { Paper } from '@material-ui/core';
import CustomCheckbox from 'components/CustomCheckbox';
import TextHeader from 'components/CustomText/TextHeader';
import CustomTextField from 'components/Input/CustomTextField';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import NumberListFormItem from './NumberListFormItem';
import { addRemoveRequest, deleteRemoveRequest, getReplacedValue, produce, isHighlight as isHL } from 'utility/general';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';
import DataListener from './DataListener';
import { useDispatch, useSelector } from 'react-redux';
import { SET_SITE_VALIDATION, SET_STUDY_VALIDATION, UPDATE_FORM_VALIDITY } from 'store/key';
import { getStore } from 'store';
import { checkNull } from 'utility/formValidate';
import { InformConsentPrincipalInvMatching } from 'utility/lookup';

const SPaperLayout = styled.div`
    padding: 10px;
`;

const SLayout = styled.div`
    padding: 10px;
`;

export default function EthicalImplicationsFormLayout({
    tabType,
    defaultData = {},
    onFormUpdate = (key, value) => null,
    disableOnPermission = false,
    applicationDetail = {},
    siteInfoId,
    formId,
    onSiteInfoUpdate = (siteInfoId, key, value) => null,
    allComments,
    getCommentDetail,
    isEnableComment,
}) {
    const [postData, setPostData] = useState({});
    const [sitePostData, setSitePostData] = useState({});
    const [studyInfoValidation, setStudyInfoValidation] = useState({});
    const [siteInfoValidation, setSiteInfoValidation] = useState({});
    const validationKey = 'ethicalImplications';

    const isStudyValid = useSelector(
        (state) => state.applicationDetailValid.isApplicationFormDetailValid?.[validationKey],
    );

    const isSiteValid = useSelector(
        (state) => state.applicationDetailValid.siteInfoValid?.[siteInfoId]?.[validationKey],
    );

    const methodologyData = useSelector(
        (state) => state.applicationDetailPostData.postData?.applicationForm?.p3Methodology,
    );

    const dispatch = useDispatch();

    // initialize the study-info postData if edit data exist
    useEffect(() => {
        if (tabType === 'info') {
            const { applicationDetailPostData } = getStore().getState();
            const postData = applicationDetailPostData.postData?.applicationForm ?? {};
            setPostData(postData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // initialize the postData if edit exist
    useEffect(() => {
        if (tabType === 'hospital') {
            const { applicationDetailPostData } = getStore().getState();
            const siteInfo = applicationDetailPostData.postData.siteInfo;
            const currentSiteInfoPostData = siteInfo?.find((item) => item.id === siteInfoId);
            setSitePostData(currentSiteInfoPostData ? { [siteInfoId]: currentSiteInfoPostData } : {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // update study-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(studyInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isStudyValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'study',
                    applicationFormId: formId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // update site-info validity to the reducer
    useEffect(() => {
        const validations = Object.values(siteInfoValidation);

        // data is not initialized
        if (validations.length === 0) {
            return;
        }

        const currentStatus = validations.every((item) => item);

        if (currentStatus !== isSiteValid) {
            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'site',
                    applicationFormId: formId,
                    siteInfoId: siteInfoId,
                    formKey: validationKey,
                    isValid: currentStatus,
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    const isMethodologyNo = () => {
        const applicationData = applicationDetail?.applicationForm?.p3Methodology;

        if ((methodologyData ?? applicationData) === 'N') {
            return true;
        }

        return false;
    };

    const updateStudyValidation = (name, isValidate) => {
        setStudyInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    const updateSiteValidation = (name, isValidate) => {
        setSiteInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    // when studyInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_STUDY_VALIDATION,
            payload: {
                [validationKey]: studyInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // when siteInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_SITE_VALIDATION,
            payload: {
                siteInfoId,
                [validationKey]: siteInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    const getData = (key) => {
        return postData[key] ?? defaultData[key];
    };

    const onFieldChange = (e) => {
        const { name, value } = e.target;

        setPostData((prevData) => {
            return {
                ...prevData,
                [name]: value,
            };
        });

        onFormUpdate(name, value);
    };

    const multiChoiceChange = (name, key, siteInfoId) => {
        if (siteInfoId) {
            onSiteFieldChange({
                target: {
                    value: key,
                    name,
                },
            });
        } else {
            onFieldChange({
                target: {
                    name,
                    value: key,
                },
            });
        }

        if (siteInfoId) {
            onSiteInfoUpdate(siteInfoId, name, key);
        } else {
            onFormUpdate(name, key);
        }
    };

    //Muilt selection logic
    const onMultipleCheckboxChange = (name, key, value, siteInfoId) => {
        let list = getP3InformConsentPrincipalInvValue();
        let multiChecked = [];
        if (list.includes(key.toString())) {
            for (let item in list) {
                if (list[item] !== key.toString()) {
                    multiChecked.push(list[item]);
                }
            }
        } else {
            multiChecked = [...list, key].flat();
        }
        if (multiChecked.length > 0) {
            multiChecked.sort((a, b) => {
                return b - a;
            });
        }
        multiChecked = InformConsentPrincipalInvMatching[multiChecked.join('')];
        if (siteInfoId) {
            onSiteFieldChange({
                target: {
                    value: multiChecked,
                    name,
                },
            });
        } else {
            onFieldChange({
                target: {
                    name,
                    value: multiChecked,
                },
            });
        }

        if (siteInfoId) {
            onSiteInfoUpdate(siteInfoId, name, multiChecked);
        } else {
            onFormUpdate(name, multiChecked);
        }
    };

    //Indicate muilt selection value in array form
    const getP3InformConsentPrincipalInvValue = () => {
        let currentValue = '';
        if (
            getSiteData('p3InformConsentPrincipalInv') !== undefined &&
            getSiteData('p3InformConsentPrincipalInv') !== null
        ) {
            currentValue =
                Object.keys(InformConsentPrincipalInvMatching).find(
                    (key) => InformConsentPrincipalInvMatching[key] === getSiteData('p3InformConsentPrincipalInv'),
                ) + '';
        }
        currentValue = currentValue.split('');
        return currentValue ?? [];
    };

    const onRiskCheckboxChange = (name, value) => {
        const { value: selectValue, key } = value[0];

        setPostData(
            produce((draft) => {
                draft[key] = selectValue;
            }),
        );

        onFormUpdate(key, selectValue);
    };

    const getDefaultData = (key) => {
        const site = applicationDetail.siteInfo.find((item) => {
            return item.id === siteInfoId;
        });

        return site?.[key];
    };

    const getSiteData = (key) => {
        return sitePostData?.[siteInfoId]?.[key] ?? getDefaultData(key);
    };

    const onSiteFieldChange = (e) => {
        const { value, name } = e.target;

        setSitePostData((prevData) => {
            const newData = produce(prevData, (draft) => {
                if (!draft[siteInfoId]) {
                    draft[siteInfoId] = {};
                }

                draft[siteInfoId][name] = value;
            });

            return newData;
        });

        onSiteInfoUpdate(siteInfoId, name, value);
    };

    const checkAtLeastOne = () => {
        const result = getData('p3AdditionalRisk');

        if (result === true) {
            const value1 = getData('p3AddRiskPhy');
            const value2 = getData('p3AddRiskPsy');
            const value3 = getData('p3AddRiskRad');
            const value4 = getData('p3AddRiskOther');

            let isValidate;

            if (value1 === true || value2 === true || value3 === true || value4 === true) {
                isValidate = true;
            } else {
                isValidate = false;
            }

            setStudyInfoValidation(
                produce((draft) => {
                    draft['checkAtLeastOne'] = isValidate;
                }),
            );
        } else {
            setStudyInfoValidation(
                produce((draft) => {
                    draft['checkAtLeastOne'] = true;
                }),
            );
        }
    };

    const isValueExistInField = (fieldKey, allowedKeys = []) => {
        const data = getData(fieldKey);

        if (allowedKeys.includes(data)) {
            return true;
        }
        return false;
    };

    const isHighlight = (keys, istable = false) => {
        return isHL(applicationDetail, siteInfoId, formId, tabType, keys, istable);
    };

    const updateSiteEthicalValidation = (value) => {
        const listOfSite = applicationDetail.siteInfo.map((item) => item.id);
        const { applicationDetailValid, applicationDetailPostData } = getStore().getState();

        const isNoOption = value === 'N';

        listOfSite.forEach((siteId) => {
            // do checking
            const currentSiteInfo = applicationDetail.siteInfo.find((item) => item.id === siteId);

            if (isNoOption) {
                applicationDetailValid.siteInfoValid[siteId].ethicalImplications = true;
            } else {
                const { p3InformConsentPrincipalInv, p3InformConsentOthersReason } = currentSiteInfo;
                const siteData = applicationDetailPostData.postData?.siteInfo?.find((item) => item.id === siteId);

                let isValid = true;

                const checkboxValue = getReplacedValue(
                    p3InformConsentPrincipalInv,
                    siteData?.p3InformConsentPrincipalInv,
                );
                const reasonValue = getReplacedValue(
                    p3InformConsentOthersReason,
                    siteData?.p3InformConsentOthersReason,
                );

                if (checkNull(checkboxValue)) {
                    isValid = false;
                }

                if (checkboxValue === 3 && checkNull(reasonValue)) {
                    isValid = false;
                }

                applicationDetailValid.siteInfoValid[siteId].ethicalImplications = isValid;
            }
        });
    };

    const updateSiteEthicalForm = (value) => {
        const isNoOption = value === 'N';

        const listOfSite = applicationDetail.siteInfo.map((item) => item.id);

        if (!isNoOption) {
            listOfSite.forEach((siteId) => {
                deleteRemoveRequest({
                    applicationFormId: formId,
                    keys: ['p3InformConsentPrincipalInv', 'p3InformConsentOthersReason'],
                    siteInfoId: siteId,
                });
            });
        } else {
            listOfSite.forEach((siteId) => {
                addRemoveRequest({
                    applicationFormId: formId,
                    name: ['p3InformConsentPrincipalInv', 'p3InformConsentOthersReason'],
                    siteInfoId: siteId,
                });
            });
        }
    };

    //check if Q17.4.1 - Q17.4.9 select yes
    const specialPrecautionValidation = () => {
        for (const [key, value] of Object.entries(defaultData)) {
            if (key.includes('p3Involve')) {
                if (value === true) {
                    return true;
                }
            }
        }
        for (const [key, value] of Object.entries(postData)) {
            if (key.includes('p3Involve')) {
                if (value === true) {
                    return true;
                }
            }
        }
        return false;
    };

    useEffect(() => {
        let list = [];
        let haveYesValue = null;
        for (const [key, value] of Object.entries(postData)) {
            if (key.includes('p3Involve')) {
                list.push(value);
            }
        }
        if (list.length > 0) {
            haveYesValue = list.some((value) => value === true);
        }
        if (haveYesValue !== null) {
            if (haveYesValue) {
                if (getData('p3SpecialPrecaution') === '') {
                    updateStudyValidation('p3SpecialPrecaution', false);
                } else {
                    updateStudyValidation('p3SpecialPrecaution', true);
                }
            } else {
                updateStudyValidation('p3SpecialPrecaution', true);
            }
        }
    }, [postData]);

    return (
        <SPaperLayout>
            <DataListener
                onPostDataReset={() => {
                    setSitePostData({});
                    setPostData({});
                }}
            />
            <Paper>
                <SLayout>
                    <TextHeader underline italic>
                        PART III: ETHICAL IMPLICATIONS
                    </TextHeader>

                    <NumberListFormItem number={'13'} label={'Information and Consent'} bold fontSize={16} />

                    {tabType === 'hospital' ? (
                        <>
                            <NumberListFormItem
                                number={'13.2'}
                                label={`Who will carry out the informed consent process with the subject?`}
                                displayContentNextLine
                                highlight={isHighlight(['p3InformConsentPrincipalInv', 'p3InformConsentOthersReason'])}
                                isEnableComment={isEnableComment}
                                commentProps={{           
                                    commentLabel: '13.2 Who will carry out the informed consent process with the subject?', 
                                    commentField: 'p3InformConsentPrincipalInv',
                                    commentDetail: getCommentDetail("p3InformConsentPrincipalInv", allComments),
                                }}
                            >
                                <div style={{ width: '100%' }}>
                                    <CustomCheckbox
                                        disableOnPermission={disableOnPermission}
                                        key={`p3InformConsentPrincipalInv${siteInfoId}`}
                                        name="p3InformConsentPrincipalInv"
                                        disabled={isMethodologyNo()}
                                        onAppFieldValidate={updateSiteValidation}
                                        allowMultipleSelect={true}
                                        remarkParams={[
                                            {
                                                key: '3',
                                                params: {
                                                    key: `p3InformConsentOthersReason${siteInfoId}`,
                                                    style: { marginLeft: 15 },
                                                    debounceChange: true,
                                                    disableOnPermission,
                                                    disabled:
                                                        // getSiteData('p3InformConsentPrincipalInv') !== 3 ||
                                                        !getP3InformConsentPrincipalInvValue().includes('3') ||
                                                        isMethodologyNo(),
                                                    updateApplicationFormOnDisableChange: formId,
                                                    updateSiteFormOnDisableChange: siteInfoId,
                                                    onAppFieldValidate: updateSiteValidation,
                                                    value: getSiteData('p3InformConsentOthersReason'),
                                                    name: 'p3InformConsentOthersReason',
                                                    onChange: onSiteFieldChange,
                                                    maxLength: 255,
                                                },
                                                SRadioLayoutStyle: {
                                                    flex: 1,
                                                },
                                            },
                                        ]}
                                        defaultData={[
                                            {
                                                key: '0',
                                                value:
                                                    // [getSiteData('p3InformConsentPrincipalInv')].includes === 1,
                                                    getP3InformConsentPrincipalInvValue().includes('0'),
                                                display: 'Principal Investigators',
                                            },
                                            {
                                                key: '1',
                                                value:
                                                    // getSiteData('p3InformConsentPrincipalInv') === 2,
                                                    getP3InformConsentPrincipalInvValue().includes('1'),
                                                display: 'Other Investigators',
                                            },
                                            {
                                                key: '2',
                                                value:
                                                    // getSiteData('p3InformConsentPrincipalInv') === 3,
                                                    getP3InformConsentPrincipalInvValue().includes('2'),
                                                display: 'Research Assistant',
                                            },
                                            {
                                                key: '3',
                                                value:
                                                    // getSiteData('p3InformConsentPrincipalInv') === 4,
                                                    getP3InformConsentPrincipalInvValue().includes('3'),
                                                display: 'Others',
                                                showRemarkInput: true,
                                            },
                                        ]}
                                        onItemChange={(key, value) => {
                                            onMultipleCheckboxChange(
                                                'p3InformConsentPrincipalInv',
                                                key,
                                                value,
                                                siteInfoId,
                                            );
                                        }}
                                        value={getP3InformConsentPrincipalInvValue()}
                                    />
                                </div>
                            </NumberListFormItem>
                        </>
                    ) : (
                        <>
                            <NumberListFormItem
                                label={`(The informed consent should state < CIRB/CREC/IRB > as one of the authorized parties to access the subjects' records related to the study for ethics review purpose.)`}
                                color={'red'}
                            />

                            <NumberListFormItem
                                style={{ display: 'flex' }}
                                number={'13.1'}
                                label={'Methodology of obtaining consent'}
                                highlight={isHighlight(['p3Methodology'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.1 Methodology of obtaining consent', 
                                    commentField: 'p3Methodology',
                                    commentDetail: getCommentDetail("p3Methodology", allComments),
                                }}
                            >
                                <CustomOutlineSelect
                                    name={'p3Methodology'}
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p3Methodology')}
                                    onChange={(e) => {
                                        onFieldChange(e);
                                        // change this field will affect values in siteInfo form

                                        // update all ethical form of the sites
                                        updateSiteEthicalForm(e.target.value);

                                        // update all ethical form validation of the sites
                                        updateSiteEthicalValidation(e.target.value);
                                    }}
                                    onAppFieldValidate={updateStudyValidation}
                                    choices={[
                                        {
                                            key: 'O',
                                            display: 'Oral',
                                        },
                                        {
                                            key: 'W',
                                            display: 'Written',
                                        },
                                        {
                                            key: 'N',
                                            display: 'No (subject to wavier by CIRB/CREC/IRB)',
                                        },
                                    ]}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'13.1.1'}
                                level={2}
                                highlight={isHighlight(['p3MethodReasons'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.1.1 State reasons if not use written consent', 
                                    commentField: 'p3MethodReasons',
                                    commentDetail: getCommentDetail("p3MethodReasons", allComments),
                                }}
                            >
                                <CustomTextField
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    value={getData('p3MethodReasons')}
                                    name={'p3MethodReasons'}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    allowAction={getData('p3Methodology') === 'O'}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    label={'State reasons if not use written consent'}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'13.1.2'}
                                level={2}
                                highlight={isHighlight(['p3MethodJustification'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.1.2 Justification for applying to waive the consent requirement', 
                                    commentField: 'p3MethodJustification',
                                    commentDetail: getCommentDetail("p3MethodJustification", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p3MethodJustification')}
                                    name={'p3MethodJustification'}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    disableOnPermission={disableOnPermission}
                                    allowAction={getData('p3Methodology') === 'N'}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    label={'Justification for applying to waive the consent requirement'}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'13.2'}
                                label={`Who will carry out the informed consent process with the subject?`}
                                displayContentNextLine
                                showDisableMessage
                                style={{ width: '100%' }}
                                highlight={isHighlight(['p3InformConsentPrincipalInv', 'p3InformConsentOthersReason'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.2 Who will carry out the informed consent process with the subject?', 
                                    commentField: 'p3InformConsentPrincipalInv',
                                    commentDetail: getCommentDetail("p3InformConsentPrincipalInv", allComments),
                                }}
                            >
                                <div style={{ width: '100%' }}>
                                    <CustomCheckbox
                                        name="p3InformConsentPrincipalInv"
                                        disableOnPermission
                                        allowMultipleSelect={true}
                                        remarkParams={[
                                            {
                                                key: 3,
                                                params: {
                                                    style: { marginLeft: 15 },
                                                    updateApplicationFormOnDisableChange: formId,
                                                    disableOnPermission: true,
                                                    // disabled: getData('p3InformConsentPrincipalInv') !== 3,
                                                    value: getData('p3InformConsentOthersReason'),
                                                    name: 'p3InformConsentOthersReason',
                                                    onChange: onFieldChange,
                                                    maxLength: 255,
                                                    debounceChange: true,
                                                },
                                                SRadioLayoutStyle: {
                                                    flex: 1,
                                                },
                                            },
                                        ]}
                                        defaultData={[
                                            {
                                                key: '0',
                                                value:
                                                    // [getSiteData('p3InformConsentPrincipalInv')].includes === 1,
                                                    getP3InformConsentPrincipalInvValue().includes('0'),
                                                display: 'Principal Investigators',
                                            },
                                            {
                                                key: '1',
                                                value:
                                                    // getSiteData('p3InformConsentPrincipalInv') === 2,
                                                    getP3InformConsentPrincipalInvValue().includes('1'),
                                                display: 'Other Investigators',
                                            },
                                            {
                                                key: '2',
                                                value:
                                                    // getSiteData('p3InformConsentPrincipalInv') === 3,
                                                    getP3InformConsentPrincipalInvValue().includes('2'),
                                                display: 'Research Assistant',
                                            },
                                            {
                                                key: '3',
                                                value:
                                                    // getSiteData('p3InformConsentPrincipalInv') === 4,
                                                    getP3InformConsentPrincipalInvValue().includes('3'),
                                                display: 'Others',
                                                showRemarkInput: true,
                                            },
                                        ]}
                                        onItemChange={(key, value) => {
                                            onMultipleCheckboxChange(
                                                'p3InformConsentPrincipalInv',
                                                key,
                                                value,
                                                siteInfoId,
                                            );
                                        }}
                                        value={getP3InformConsentPrincipalInvValue()}
                                    />
                                </div>
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'13.3'}
                                label={'Will an interpreter be available when required?'}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                allowAction={isValueExistInField('p3Methodology', ['O', 'W'])}
                                updateApplicationFormOnDisableChange={formId}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3Interpreter')}
                                name={'p3Interpreter'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3Interpreter'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.3 Will an interpreter be available when required?', 
                                    commentField: 'p3Interpreter',
                                    commentDetail: getCommentDetail("p3Interpreter", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'13.3.1'}
                                level={2}
                                highlight={isHighlight(['p3InterpreterJustification'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.3.1 If no, justification', 
                                    commentField: 'p3InterpreterJustification',
                                    commentDetail: getCommentDetail("p3InterpreterJustification", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p3InterpreterJustification')}
                                    name={'p3InterpreterJustification'}
                                    disableOnPermission={disableOnPermission}
                                    onChange={onFieldChange}
                                    maxLength={255}
                                    updateApplicationFormOnDisableChange={formId}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={
                                        getData('p3Interpreter') === false &&
                                        isValueExistInField('p3Methodology', ['O', 'W'])
                                    }
                                    label={'If no, justification'}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'13.4'}
                                displayContentNextLine
                                disableVerticalCenter
                                label={`In obtaining informed consent from subjects, what is the minimal time given to a subject to consider after explanation has been given?`}
                                labelTooltip={'≥15 mins is suggested'}
                                highlight={isHighlight(['p3MinTime'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.4 In obtaining informed consent from subjects, what is the minimal time given to a subject to consider after explanation has been given?', 
                                    commentField: 'p3MinTime',
                                    commentDetail: getCommentDetail("p3MinTime", allComments),
                                }}
                            >
                                <CustomTextField
                                    value={getData('p3MinTime')}
                                    name={'p3MinTime'}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={255}
                                    debounceChange
                                    onAppFieldValidate={updateStudyValidation}
                                    updateApplicationFormOnDisableChange={formId}
                                    allowAction={isValueExistInField('p3Methodology', ['O', 'W'])}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'13.5'}
                                highlight={isHighlight(['p3Arrangement'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '13.5 If subjects are incompetent in giving consent, what would be the arrangement?', 
                                    commentField: 'p3Arrangement',
                                    commentDetail: getCommentDetail("p3Arrangement", allComments),
                                }}
                            >
                                <CustomTextField
                                    value={getData('p3Arrangement')}
                                    name={'p3Arrangement'}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    onChange={onFieldChange}
                                    onAppFieldValidate={updateStudyValidation}
                                    allowAction={isValueExistInField('p3Methodology', ['O', 'W'])}
                                    updateApplicationFormOnDisableChange={formId}
                                    label={
                                        'If subjects are incompetent in giving consent, what would be the arrangement?'
                                    }
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'14'}
                                label={'Data and Safety Monitoring'}
                                bold
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />

                            <NumberListFormItem
                                number={'14.1'}
                                showTrueFalse
                                label={`Will an independent committee review data and safety of study?`}
                                disableOnPermission={disableOnPermission}
                                debounceChange
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3IndepCommittee')}
                                name={'p3IndepCommittee'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3IndepCommittee'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '14.1 Will an independent committee review data and safety of study?', 
                                    commentField: 'p3IndepCommittee',
                                    commentDetail: getCommentDetail("p3IndepCommittee", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'14.1.1'}
                                level={2}
                                highlight={isHighlight(['p3IndepCommitteeJustification'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '14.1.1 If no, justification', 
                                    commentField: 'p3IndepCommitteeJustification',
                                    commentDetail: getCommentDetail("p3IndepCommitteeJustification", allComments),
                                }}
                            >
                                <CustomTextField
                                    allowAction={getData('p3IndepCommittee') === false}
                                    updateApplicationFormOnDisableChange={formId}
                                    maxLength={500}
                                    isTextArea
                                    textAreaMinRow={3}
                                    debounceChange
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p3IndepCommitteeJustification')}
                                    onAppFieldValidate={updateStudyValidation}
                                    name={'p3IndepCommitteeJustification'}
                                    onChange={onFieldChange}
                                    label={'If no, justification'}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'15'}
                                bold
                                fontSize={16}
                                highlight={isHighlight(['p3Ethical'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '15 Major Ethical Issues', 
                                    commentField: 'p3Ethical',
                                    commentDetail: getCommentDetail("p3Ethical", allComments),
                                }}
                            >
                                <CustomTextField
                                    value={getData('p3Ethical')}
                                    maxLength={6000}
                                    isTextArea
                                    textAreaMinRow={12}
                                    name={'p3Ethical'}
                                    disableOnPermission={disableOnPermission}
                                    debounceChange
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    tooltip={
                                        'Use language that can be understood by laypersons. Technical terms are not recommended and no referral to protocols/other documents.'
                                    }
                                    label={'Major Ethical Issues'}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'16'}
                                label={'Preference on Review Channel'}
                                bold
                                style={{ marginTop: 30 }}
                                fontSize={16}
                            />
                            <NumberListFormItem
                                number={'16.1'}
                                label={`Applicant's Preference`}
                                highlight={isHighlight(['p3PreferenceFull'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: `16.1 Applicant's Preference`, 
                                    commentField: 'p3PreferenceFull',
                                    commentDetail: getCommentDetail("p3PreferenceFull", allComments),
                                }}
                            >
                                <CustomCheckbox
                                    radioButton
                                    defaultData={[
                                        {
                                            key: true,
                                            value: getData('p3PreferenceFull') === true,
                                            display: 'Full Review',
                                        },
                                        {
                                            key: false,
                                            value: getData('p3PreferenceFull') === true,
                                            display: 'Expedited Review',
                                        },
                                    ]}
                                    disableOnPermission={disableOnPermission}
                                    onAppFieldValidate={updateStudyValidation}
                                    name={'p3PreferenceFull'}
                                    value={[getData('p3PreferenceFull')]}
                                    onChange={(name, value) => {
                                        const selectedObject = value.find((item) => item.value);

                                        setPostData(
                                            produce((draft) => {
                                                draft[name] = selectedObject.key;
                                            }),
                                        );

                                        onFormUpdate(name, selectedObject.key);
                                    }}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'16.2'}
                                highlight={isHighlight(['p3Justification'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '16.2 Justification', 
                                    commentField: 'p3Justification',
                                    commentDetail: getCommentDetail("p3Justification", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    label={'Justification'}
                                    disableOnPermission={disableOnPermission}
                                    value={getData('p3Justification')}
                                    onAppFieldValidate={updateStudyValidation}
                                    name={'p3Justification'}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    onChange={onFieldChange}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                color={'red'}
                                label={
                                    '(The Central IRB / CRECs/IRB has full authority to decide the type of ethical review to be conducted)'
                                }
                            />

                            <NumberListFormItem
                                number={'17'}
                                bold
                                label={`Risk Assessment`}
                                fontSize={16}
                                style={{ marginTop: 30 }}
                            />

                            <NumberListFormItem
                                number={'17.1'}
                                label={`Will study incur extra clinical intervention(s) to subjects?`}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                trueFalseValue={getData('p3ExtraClinical')}
                                onAppFieldValidate={updateStudyValidation}
                                name={'p3ExtraClinical'}
                                onTrueFalseSelected={onFieldChange}
                                labelWidth={350}
                                highlight={isHighlight(['p3ExtraClinical'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.1 Will study incur extra clinical intervention(s) to subjects?', 
                                    commentField: 'p3ExtraClinical',
                                    commentDetail: getCommentDetail("p3ExtraClinical", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.2'}
                                label={`Will study impose additional risk to subjects?`}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3AdditionalRisk')}
                                name={'p3AdditionalRisk'}
                                onTrueFalseSelected={onFieldChange}
                                labelWidth={350}
                                highlight={isHighlight(['p3AdditionalRisk'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.2 Will study impose additional risk to subjects?', 
                                    commentField: 'p3AdditionalRisk',
                                    commentDetail: getCommentDetail("p3AdditionalRisk", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.2.1'}
                                level={2}
                                label={`If yes, please select the following where applicable and provide details.`}
                                highlight={isHighlight([
                                    'p3AddRiskPhy',
                                    'p3AddRiskPhyReason',
                                    'p3AddRiskPsy',
                                    'p3AddRiskPsyReason',
                                    'p3AddRiskRad',
                                    'p3AddRiskRadReason',
                                    'p3AddRiskOther',
                                    'p3AddRiskOtherReason',
                                ])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.2.1 If yes, please select the following where applicable and provide details.', 
                                    commentField: 'p3AddRiskPhy',
                                    commentDetail: getCommentDetail("p3AddRiskPhy", allComments),
                                }}
                            />

                            <NumberListFormItem level={2}>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <CustomCheckbox
                                        allowAction={getData('p3AdditionalRisk') === true}
                                        disableOnPermission={disableOnPermission}
                                        allowUnCheckAll
                                        name={'p3AddRiskPhy'}
                                        onAppFieldValidate={checkAtLeastOne}
                                        updateApplicationFormOnDisableChange={formId}
                                        defaultData={[{ key: 'p3AddRiskPhy', value: false, display: '' }]}
                                        value={[getData('p3AddRiskPhy') ? 'p3AddRiskPhy' : '']}
                                        onChange={onRiskCheckboxChange}
                                    />
                                    <CustomTextField
                                        label={'Physical'}
                                        updateApplicationFormOnDisableChange={formId}
                                        disableOnPermission={disableOnPermission}
                                        debounceChange
                                        allowAction={
                                            getData('p3AdditionalRisk') === true && getData('p3AddRiskPhy') === true
                                        }
                                        onAppFieldValidate={updateStudyValidation}
                                        maxLength={6000}
                                        isTextArea
                                        textAreaMinRow={12}
                                        name={'p3AddRiskPhyReason'}
                                        onChange={onFieldChange}
                                        value={getData('p3AddRiskPhyReason')}
                                    />
                                </div>
                            </NumberListFormItem>

                            <NumberListFormItem level={2}>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <CustomCheckbox
                                        allowAction={getData('p3AdditionalRisk') === true}
                                        disableOnPermission={disableOnPermission}
                                        allowUnCheckAll
                                        name={'p3AddRiskPsy'}
                                        onAppFieldValidate={checkAtLeastOne}
                                        updateApplicationFormOnDisableChange={formId}
                                        defaultData={[{ key: 'p3AddRiskPsy', value: false, display: '' }]}
                                        value={[getData('p3AddRiskPsy') ? 'p3AddRiskPsy' : '']}
                                        onChange={onRiskCheckboxChange}
                                    />
                                    <CustomTextField
                                        label={'Psychological'}
                                        debounceChange
                                        disableOnPermission={disableOnPermission}
                                        allowAction={
                                            getData('p3AdditionalRisk') === true && getData('p3AddRiskPsy') === true
                                        }
                                        onAppFieldValidate={updateStudyValidation}
                                        updateApplicationFormOnDisableChange={formId}
                                        maxLength={6000}
                                        isTextArea
                                        textAreaMinRow={12}
                                        name={'p3AddRiskPsyReason'}
                                        onChange={onFieldChange}
                                        value={getData('p3AddRiskPsyReason')}
                                    />
                                </div>
                            </NumberListFormItem>

                            <NumberListFormItem level={2}>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <CustomCheckbox
                                        allowAction={getData('p3AdditionalRisk') === true}
                                        allowUnCheckAll
                                        disableOnPermission={disableOnPermission}
                                        onAppFieldValidate={checkAtLeastOne}
                                        name={'p3AddRiskRad'}
                                        updateApplicationFormOnDisableChange={formId}
                                        defaultData={[{ key: 'p3AddRiskRad', value: false, display: '' }]}
                                        value={[getData('p3AddRiskRad') ? 'p3AddRiskRad' : '']}
                                        onChange={onRiskCheckboxChange}
                                    />
                                    <CustomTextField
                                        label={'Radiation'}
                                        debounceChange
                                        disableOnPermission={disableOnPermission}
                                        allowAction={
                                            getData('p3AdditionalRisk') === true && getData('p3AddRiskRad') === true
                                        }
                                        onAppFieldValidate={updateStudyValidation}
                                        updateApplicationFormOnDisableChange={formId}
                                        maxLength={6000}
                                        isTextArea
                                        textAreaMinRow={12}
                                        name={'p3AddRiskRadReason'}
                                        onChange={onFieldChange}
                                        value={getData('p3AddRiskRadReason')}
                                    />
                                </div>
                            </NumberListFormItem>

                            <NumberListFormItem level={2}>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <CustomCheckbox
                                        allowAction={getData('p3AdditionalRisk') === true}
                                        disableOnPermission={disableOnPermission}
                                        allowUnCheckAll
                                        name={'p3AddRiskOther'}
                                        onAppFieldValidate={checkAtLeastOne}
                                        updateApplicationFormOnDisableChange={formId}
                                        defaultData={[{ key: 'p3AddRiskOther', value: false, display: '' }]}
                                        value={[getData('p3AddRiskOther') ? 'p3AddRiskOther' : '']}
                                        onChange={onRiskCheckboxChange}
                                    />
                                    <CustomTextField
                                        label={'Others'}
                                        disableOnPermission={disableOnPermission}
                                        updateApplicationFormOnDisableChange={formId}
                                        onAppFieldValidate={updateStudyValidation}
                                        debounceChange
                                        allowAction={
                                            getData('p3AdditionalRisk') === true && getData('p3AddRiskOther') === true
                                        }
                                        maxLength={6000}
                                        isTextArea
                                        textAreaMinRow={12}
                                        name={'p3AddRiskOtherReason'}
                                        onChange={onFieldChange}
                                        value={getData('p3AddRiskOtherReason')}
                                    />
                                </div>
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'17.3'}
                                label={`Will study raise privacy concerns?`}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3Privacy')}
                                name={'p3Privacy'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3Privacy'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.3 Will study raise privacy concerns?', 
                                    commentField: 'p3Privacy',
                                    commentDetail: getCommentDetail("p3Privacy", allComments),
                                }}
                            />
                            <NumberListFormItem
                                number={'17.4'}
                                label={`Will the study involve the following vulnerable subjects?`}
                            />

                            <NumberListFormItem
                                number={'17.4.1'}
                                level={2}
                                label={`Children or adolescent (of less than 18-year-old)`}
                                disableOnPermission={disableOnPermission}
                                showTrueFalse
                                // displayContentNextLine
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolveChildren')}
                                name={'p3InvolveChildren'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolveChildren'])}
                                labelWidth={300}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.1 Children or adolescent (of less than 18-year-old)', 
                                    commentField: 'p3InvolveChildren',
                                    commentDetail: getCommentDetail("p3InvolveChildren", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.2'}
                                level={2}
                                label={`Illiterates`}
                                showTrueFalse
                                // displayContentNextLine
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolveIlliterates')}
                                name={'p3InvolveIlliterates'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolveIlliterates'])}
                                labelWidth={300}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.2 Illiterates', 
                                    commentField: 'p3InvolveIlliterates',
                                    commentDetail: getCommentDetail("p3InvolveIlliterates", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.3'}
                                level={2}
                                label={`Mentally incapacitated persons`}
                                showTrueFalse
                                labelWidth={300}
                                // displayContentNextLine
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolveIncapacitated')}
                                name={'p3InvolveIncapacitated'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolveIncapacitated'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.3 Mentally incapacitated persons', 
                                    commentField: 'p3InvolveIncapacitated',
                                    commentDetail: getCommentDetail("p3InvolveIncapacitated", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.4'}
                                level={2}
                                label={`Impoverished persons`}
                                showTrueFalse
                                // displayContentNextLine
                                disableOnPermission={disableOnPermission}
                                labelWidth={300}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolveImpoverished')}
                                name={'p3InvolveImpoverished'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolveImpoverished'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.4 Impoverished persons', 
                                    commentField: 'p3InvolveImpoverished',
                                    commentDetail: getCommentDetail("p3InvolveImpoverished", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.5'}
                                level={2}
                                label={`Ethnic minority groups`}
                                showTrueFalse
                                // displayContentNextLine
                                labelWidth={300}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolveEthnic')}
                                name={'p3InvolveEthnic'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolveEthnic'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.5 Ethnic minority groups', 
                                    commentField: 'p3InvolveEthnic',
                                    commentDetail: getCommentDetail("p3InvolveEthnic", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.6'}
                                level={2}
                                label={`Patients in emergency conditions`}
                                showTrueFalse
                                // displayContentNextLine
                                labelWidth={300}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolvePatients')}
                                name={'p3InvolvePatients'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolvePatients'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.6 Patients in emergency conditions', 
                                    commentField: 'p3InvolvePatients',
                                    commentDetail: getCommentDetail("p3InvolvePatients", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.7'}
                                level={2}
                                label={`Prisoners`}
                                showTrueFalse
                                // displayContentNextLine
                                labelWidth={300}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolvePrisoners')}
                                name={'p3InvolvePrisoners'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolvePrisoners'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.7 Prisoners', 
                                    commentField: 'p3InvolvePrisoners',
                                    commentDetail: getCommentDetail("p3InvolvePrisoners", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.8'}
                                level={2}
                                label={`Subordinates or students of investigators`}
                                showTrueFalse
                                // displayContentNextLine
                                labelWidth={300}
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3InvolveSubordinates')}
                                name={'p3InvolveSubordinates'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolveSubordinates'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.8 Subordinates or students of investigators', 
                                    commentField: 'p3InvolveSubordinates',
                                    commentDetail: getCommentDetail("p3InvolveSubordinates", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.9'}
                                level={2}
                                label={`Others`}
                                onAppFieldValidate={updateStudyValidation}
                                showTrueFalse
                                // displayContentNextLine
                                labelWidth={300}
                                disableOnPermission={disableOnPermission}
                                trueFalseValue={getData('p3InvolveOthers')}
                                name={'p3InvolveOthers'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3InvolveOthers'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.9 Others', 
                                    commentField: 'p3InvolveOthers',
                                    commentDetail: getCommentDetail("p3InvolveOthers", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.4.9.1'}
                                level={3}
                                highlight={isHighlight(['p3InvolveOtherSpecify'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.4.9.1 Specify', 
                                    commentField: 'p3InvolveOtherSpecify',
                                    commentDetail: getCommentDetail("p3InvolveOtherSpecify", allComments),
                                }}
                            >
                                <CustomTextField
                                    label={'Specify'}
                                    debounceChange
                                    value={getData('p3InvolveOtherSpecify')}
                                    name={'p3InvolveOtherSpecify'}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={255}
                                    onAppFieldValidate={updateStudyValidation}
                                    onChange={onFieldChange}
                                    allowAction={getData('p3InvolveOthers') === true}
                                    updateApplicationFormOnDisableChange={formId}
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'17.5'}
                                highlight={isHighlight(['p3SpecialPrecaution'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.5 Are there any special precautions to protect the interest of vulnerable subjects?', 
                                    commentField: 'p3SpecialPrecaution',
                                    commentDetail: getCommentDetail("p3SpecialPrecaution", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p3SpecialPrecaution')}
                                    name={'p3SpecialPrecaution'}
                                    disableOnPermission={disableOnPermission}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    onAppFieldValidate={(name, valid) => {
                                        let yesSelected = specialPrecautionValidation();
                                        if (yesSelected === true) {
                                            updateStudyValidation(name, valid);
                                        } else {
                                            updateStudyValidation(name, true);
                                        }
                                    }}
                                    onChange={onFieldChange}
                                    label={
                                        'Are there any special precautions to protect the interest of vulnerable subjects?'
                                    }
                                />
                            </NumberListFormItem>

                            <NumberListFormItem
                                number={'17.6'}
                                label={`Will study impose potential hazard to clinical staff?`}
                                showTrueFalse
                                disableOnPermission={disableOnPermission}
                                onAppFieldValidate={updateStudyValidation}
                                trueFalseValue={getData('p3Hazard')}
                                name={'p3Hazard'}
                                onTrueFalseSelected={onFieldChange}
                                highlight={isHighlight(['p3Hazard'])}
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.6 Will study impose potential hazard to clinical staff?', 
                                    commentField: 'p3Hazard',
                                    commentDetail: getCommentDetail("p3Hazard", allComments),
                                }}
                            />

                            <NumberListFormItem
                                number={'17.6.1'}
                                level={2}
                                highlight={isHighlight(['p3Control'])}
                                alignItems="flex-start"
                                isEnableComment={isEnableComment}
                                commentProps={{ 
                                    commentLabel: '17.6.1 Control measures to protect the clinical staff', 
                                    commentField: 'p3Control',
                                    commentDetail: getCommentDetail("p3Control", allComments),
                                }}
                            >
                                <CustomTextField
                                    debounceChange
                                    value={getData('p3Control')}
                                    name={'p3Control'}
                                    disableOnPermission={disableOnPermission}
                                    onChange={onFieldChange}
                                    maxLength={1000}
                                    isTextArea
                                    textAreaMinRow={4}
                                    allowAction={getData('p3Hazard') === true}
                                    onAppFieldValidate={updateStudyValidation}
                                    label={'Control measures to protect the clinical staff'}
                                    updateApplicationFormOnDisableChange={formId}
                                />
                            </NumberListFormItem>
                        </>
                    )}
                </SLayout>
            </Paper>
        </SPaperLayout>
    );
}
